import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { REQUEST_RESULT } from 'enums';
import usePageable from 'hooks/usePageable';
import type Pageable from 'models/Pageable';
import type { AnchorDealerVOModel } from 'models/vo/AnchorDealerVO';
import { setFormValues } from 'utils/form/setFormValues';
import {
  requestFinancierAnchorDealerDecline,
  requestFinancierAnchorDealerList,
} from 'utils/http/api/financier/anchor-dealers';
import type { FinancierAnchorDealerListRequest } from 'utils/http/api/financier/anchor-dealers/request';
import useModal from 'utils/modal/useModal';
import { addSearchParams, getParsedSearchParams, makeSearchParamsPattern } from 'utils/searchParams';

const FI_DEALER_ASSIGNMENT_ASSIGNED_LIST_QS_KEY = 'fi-dealer-assign-assigned-list';
const FI_DEALER_ASSIGNMENT_DECLINED_LIST_QS_KEY = 'fi-dealer-assign-declined-list';

function useFinancierAnchorDealerAssignmentListState() {
  const modal = useModal();

  const assignedTabSearchForm = useForm<FinancierAnchorDealerListRequest>();
  const declinedTabSearchForm = useForm<FinancierAnchorDealerListRequest>();

  const [assignedAnchorDealerData, setAssignedAnchorDealerData] = useState<Pageable<AnchorDealerVOModel[]>>();
  const [declinedAnchorDealerData, setDeclinedAnchorDealerData] = useState<Pageable<AnchorDealerVOModel[]>>();

  const { pageable: assignedTabPageable, setPageable: setAssignedTabPageable } = usePageable(
    FI_DEALER_ASSIGNMENT_ASSIGNED_LIST_QS_KEY,
  );
  const { pageable: declinedTabPageable, setPageable: setDeclinedTabPageable } = usePageable(
    FI_DEALER_ASSIGNMENT_DECLINED_LIST_QS_KEY,
  );

  const fetchAll = async (): Promise<void> => {
    setFormValues<FinancierAnchorDealerListRequest>(
      assignedTabSearchForm.setValue,
      getParsedSearchParams(FI_DEALER_ASSIGNMENT_ASSIGNED_LIST_QS_KEY).formSearchData,
    );
    setFormValues<FinancierAnchorDealerListRequest>(
      declinedTabSearchForm.setValue,
      getParsedSearchParams(FI_DEALER_ASSIGNMENT_DECLINED_LIST_QS_KEY).formSearchData,
    );

    const addFiAnchorDealerAssignmentListQs = () => {
      const waitingQs = makeSearchParamsPattern(
        {
          ...assignedTabSearchForm.getValues(),
          pageNumber: assignedTabPageable.currentPage,
          rowCount: assignedTabPageable.sizePerPage,
        },
        FI_DEALER_ASSIGNMENT_ASSIGNED_LIST_QS_KEY,
      );
      const completedQs = makeSearchParamsPattern(
        {
          ...declinedTabSearchForm.getValues(),
          pageNumber: declinedTabPageable.currentPage,
          rowCount: declinedTabPageable.sizePerPage,
        },
        FI_DEALER_ASSIGNMENT_DECLINED_LIST_QS_KEY,
      );
      addSearchParams(waitingQs + completedQs);
    };
    addFiAnchorDealerAssignmentListQs();

    try {
      const [assignedAnchorDealerResponse, declinedAnchorDealerResponse] = await Promise.all([
        requestFinancierAnchorDealerList(assignedTabPageable.currentPage, assignedTabPageable.sizePerPage, {
          assigned: true,
          declined: false,
        }),
        requestFinancierAnchorDealerList(declinedTabPageable.currentPage, declinedTabPageable.sizePerPage, {
          assigned: true,
          declined: true,
        }),
      ]);

      setAssignedAnchorDealerData(assignedAnchorDealerResponse);
      setAssignedTabPageable(assignedAnchorDealerResponse);
      setDeclinedAnchorDealerData(declinedAnchorDealerResponse);
      setDeclinedTabPageable(declinedAnchorDealerResponse);
    } catch (error) {
      modal.show(error);
    }
  };

  const fetchFiAnchorDealerAssignedList = async (
    pageNumber: number,
    rowCount: number,
    data: FinancierAnchorDealerListRequest,
  ) => {
    try {
      const assignedAnchorDealerResponse = await requestFinancierAnchorDealerList(pageNumber, rowCount, {
        ...data,
        assigned: true,
        declined: false,
      });

      setAssignedAnchorDealerData(assignedAnchorDealerResponse);
      setAssignedTabPageable(assignedAnchorDealerResponse);
    } catch (error) {
      modal.show(error);
    }
  };

  const handleClickSearchAssignedList = async () => {
    await fetchFiAnchorDealerAssignedList(0, assignedTabPageable.sizePerPage, assignedTabSearchForm.getValues());
  };

  const paginateAssignedList = async (pageNumber: number, rowCount: number) => {
    await fetchFiAnchorDealerAssignedList(
      pageNumber,
      rowCount,
      getParsedSearchParams(FI_DEALER_ASSIGNMENT_ASSIGNED_LIST_QS_KEY).formSearchData,
    );
  };

  const fetchFiAnchorDealerDeclinedList = async (
    pageNumber: number,
    rowCount: number,
    data: FinancierAnchorDealerListRequest,
  ) => {
    try {
      const declinedAnchorDealerResponse = await requestFinancierAnchorDealerList(pageNumber, rowCount, {
        ...data,
        assigned: true,
        declined: true,
      });

      setDeclinedAnchorDealerData(declinedAnchorDealerResponse);
      setDeclinedTabPageable(declinedAnchorDealerResponse);
    } catch (error) {
      modal.show(error);
    }
  };

  const handleClickSearchDeclinedList = async () => {
    await fetchFiAnchorDealerDeclinedList(0, declinedTabPageable.sizePerPage, declinedTabSearchForm.getValues());
  };

  const paginateDeclinedList = async (pageNumber: number, rowCount: number) => {
    await fetchFiAnchorDealerDeclinedList(
      pageNumber,
      rowCount,
      getParsedSearchParams(FI_DEALER_ASSIGNMENT_DECLINED_LIST_QS_KEY).formSearchData,
    );
  };

  const requestDecline = async (anchorDealerIds: number[], reason: string): Promise<REQUEST_RESULT> => {
    try {
      await requestFinancierAnchorDealerDecline(anchorDealerIds, reason);
      fetchAll();

      return REQUEST_RESULT.SUCCESS;
    } catch (e) {
      modal.show(e);

      return REQUEST_RESULT.FAIL;
    }
  };

  return {
    fetchAll,
    declinedTabUtils: {
      declinedAnchorDealerData,
      declinedTabPageable,
      declinedTabSearchForm,
      handleClickSearchDeclinedList,
      paginateDeclinedList,
    },
    assignedTabUtils: {
      assignedAnchorDealerData,
      assignedTabPageable,
      assignedTabSearchForm,
      handleClickSearchAssignedList,
      paginateAssignedList,
      requestDecline,
    },
  };
}
export default useFinancierAnchorDealerAssignmentListState;
